import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { trackEvent } from "@features/tracking/tracking"
import { usePeopleGrid } from "@hooks/usePeopleGrid"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, InnerBlocks, useInlineEditableValue } from "eddev/blocks"
import { AnimatePresence, motion } from "motion/react"
import { useId } from "react"
import { createPortal } from "react-dom"

export const meta: BlockMeta = {
  title: "People Grid Item",
  icon: "admin-users",
  category: "layouts",
  description: "A single person in a grid of people with images, titles and bios.",
}

export default defineBlock("content/people-grid-item", (props) => {
  const id = useId()
  const peopleGrid = usePeopleGrid()

  const selected = peopleGrid.activeItem === id

  const [name] = useInlineEditableValue<string>("name")

  return (
    <>
      <div
        className={cn("group hover-card cursor-pointer", selected && "hover-card-hovering")}
        onClick={(e) => {
          peopleGrid.onSelect(id)
          trackEvent("Expand People Grid Item", {
            Person: name,
            Block: "People Grid Item",
          })
        }}
        data-mixpanel-block="People Grid"
      >
        <div className="aspect-square bg-bg-mid relative overflow-hidden transition-[border-radius] group-hover:rounded-md [.hover-card-hovering_&]:rounded-md">
          <ResponsiveImage {...props.image} max />
        </div>
        <div className="pt-2 gap-1">
          <EditableText as="p" store="name" placeholder="Enter this persons name" />
          <Chip shape="rounded" variant="high">
            <EditableText store="title" placeholder="Role" />
          </Chip>
        </div>
        <div className="mt-4 flex items-stretch justify-stretch opacity-0 group-hover:opacity-100 [.hover-card-hovering_&]:opacity-100 transition-opacity duration-150 ">
          <Button variant="primary" className="w-full" shape="small">
            <AnimatePresence mode="wait">
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={selected ? "selected" : "unselected"}
              >
                {selected ? "Close" : "Read more"}
              </motion.span>
            </AnimatePresence>
          </Button>
        </div>
      </div>

      {selected &&
        !!peopleGrid.renderTarget &&
        createPortal(
          <div className="theme-dark bg-bg text-fg rounded-md p-6 relative">
            <div className="md:w-[55%]">
              <EditableText as="p" store="name" placeholder="Enter this persons name" className="type-title-l pb-2" />
              <div className="pb-4">
                <Chip shape="rounded" variant="high">
                  <EditableText store="title" placeholder="Role" />
                </Chip>
              </div>
            </div>
            <div className="md:w-[55%]">
              <InnerBlocks allowedBlocks={["#richtext"]} />
            </div>
            <div className="hidden md:block absolute top-3 right-3 bottom-3 aspect-[0.8]">
              <ResponsiveImage {...props.image} max className="rounded-tr-sm rounded-br-sm overflow-hidden" />
            </div>
          </div>,
          peopleGrid.renderTarget,
        )}
    </>
  )
})
